import { useEffect, useRef, useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ServicesSection from './components/ServicesSection';

const STRENGTHS = [
  {
    icon: 'ri-shield-check-line',
    title: 'セキュリティを最優先に考えるIT設計',
    desc: '家庭用ルーターのまま運用されているオフィスや、多拠点・多人数環境でも、実情に合った現実的なセキュリティ対策をご提案します。',
  },
  {
    icon: 'ri-settings-3-line',
    title: '中小企業にちょうどいいIT支援',
    desc: '過剰なシステムや高額な提案は行いません。会社規模・業種・運用体制に合わせた、「使いこなせるIT」を大切にしています。',
  },
  {
    icon: 'ri-computer-line',
    title: 'オフィスのITを丸ごと任せられる',
    desc: 'PC・複合機・ネットワーク構築・AI活用まで、窓口は一つ。オフィスのIT担当者の代わりとして伴走します。',
  },
];

const COMPANY_INFO = [
  { label: '代表取締役', value: '森　尚樹' },
  { label: '会社名', value: '株式会社湘南ビジネスサポート' },
  { label: '英語表記', value: 'Shonan Business Support Co., Ltd.' },
  { label: '営業所', value: '神奈川県大和市南林間２－１８－４' },
  { label: '事業内容', value: 'オフィスIT機器・ネットワーク構築、情報セキュリティ対策、IT・DX支援' },
  { label: '対応エリア', value: '神奈川県全域・東京都・全国対応可' },
];

const INQUIRY_TYPES = [
  'オフィスIT機器・ネットワーク構築について',
  '情報セキュリティ対策について',
  'IT・DX支援について',
  'その他',
];

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function HomePage() {
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const observers = sectionRefs.current.map((el) => {
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) { el.classList.add('animate-in'); obs.disconnect(); }
        },
        { threshold: 0.1 }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((obs) => obs?.disconnect());
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    const form = e.currentTarget;
    setFormStatus('submitting');
    const data = new URLSearchParams();
    new FormData(form).forEach((value, key) => data.append(key, value as string));
    try {
      const res = await fetch('https://readdy.ai/api/form/d70e4rj2m2156g4s6ra0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) { setFormStatus('success'); form.reset(); setCharCount(0); }
      else setFormStatus('error');
    } catch { setFormStatus('error'); }
  };

  return (
    <div className="bg-[#F8FCFF] text-[#1A2E3A]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
      <Navbar />

      {/* ─── HERO ─── */}
      <section id="hero" className="relative h-screen min-h-[700px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/93514b094d2be0d7b687fd3320bc3b24.jpg"
            alt="湘南ビジネスサポート"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A2840]/75 via-[#0D3550]/45 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A2840]/50 via-transparent to-transparent" />
        </div>
        <div className="relative z-10 w-full px-10 lg:px-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 mb-6 opacity-0 translate-y-4" style={{ animation: 'fadeUp 0.8s ease 0.1s forwards' }}>
              <div className="w-5 h-px bg-[#7EC8E8]" />
              <p className="text-[#7EC8E8] text-xs tracking-[0.4em]" style={{ fontFamily: "'Inter', sans-serif" }}>SHONAN BUSINESS SUPPORT</p>
              <div className="w-5 h-px bg-[#7EC8E8]" />
            </div>
            <h1
              className="text-white text-4xl lg:text-6xl font-light leading-tight mb-6 opacity-0 translate-y-4"
              style={{ fontFamily: "'Inter', 'Noto Sans JP', sans-serif", animation: 'fadeUp 0.8s ease 0.35s forwards' }}
            >
              オフィスのIT・<br />
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #7EC8E8, #B0E8F8)' }}>
                セキュリティを、任せてください。
              </span>
            </h1>
            <p className="text-white/75 text-base leading-loose tracking-wider mb-10 opacity-0 translate-y-4" style={{ animation: 'fadeUp 0.8s ease 0.55s forwards' }}>
              「誰に相談すればいいかわからない」<br />
              中小企業のオフィスIT課題を一社完結で解決します。
            </p>
            <div className="flex gap-4 opacity-0 translate-y-4" style={{ animation: 'fadeUp 0.8s ease 0.75s forwards' }}>
              <button
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-3.5 bg-[#2B8DB8] text-white text-xs tracking-widest rounded-full hover:bg-[#1E6E94] transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                サービスを見る
              </button>
              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-3.5 border border-white/60 text-white text-xs tracking-widest rounded-full hover:bg-white/15 backdrop-blur-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                無料相談を申し込む
              </button>
            </div>
            <div className="mt-10 flex flex-col gap-4 opacity-0 translate-y-4" style={{ animation: 'fadeUp 0.8s ease 0.95s forwards' }}>
              <div className="w-10 h-px bg-[#7EC8E8]/60" />
              <p className="text-white/80 text-sm leading-relaxed tracking-widest" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                ITの「困った」を、ぜんぶ引き受けます。
              </p>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-1">
                {['機器・ネットワーク構築', '情報セキュリティ対策', 'AI・DX活用支援'].map((tag, i) => (
                  <div key={i} className="flex items-center gap-2">
                    {i !== 0 && <span className="w-px h-3 bg-white/25" />}
                    <span className="text-white/60 text-xs tracking-wider" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>{tag}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className="text-white/50 text-xs tracking-widest" style={{ fontFamily: "'Inter', sans-serif" }}>SCROLL</span>
          <div className="w-px h-12 bg-gradient-to-b from-white/50 to-transparent animate-pulse" />
        </div>
      </section>

      {/* ─── ABOUT ─── */}
      <section id="about" className="py-28 px-10 lg:px-20">
        <div ref={(el) => { sectionRefs.current[0] = el; }} className="flex flex-col lg:flex-row gap-16 items-center opacity-0 translate-y-8 transition-all duration-700">
          <div className="lg:w-1/2">
            <div className="w-full h-96 rounded-2xl overflow-hidden bg-gradient-to-br from-[#DFF0FB] via-[#EEF7FC] to-[#C8E4F2] flex items-center justify-center">
              <img
                src="https://storage.readdy-site.link/project_files/0f42ec31-d307-4389-8acb-71e36bc515fe/317c329d-21d9-4ef9-baf3-2379f307b5d9_color.png?v=aad451d767a67b918851c901b8c9b6af"
                alt="株式会社湘南ビジネスサポート"
                className="w-3/4 h-auto object-contain"
              />
            </div>
          </div>
          <div className="lg:w-1/2">
            <p className="text-[#2B8DB8] text-xs tracking-[0.4em] mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>ABOUT US</p>
            <h2 className="text-3xl lg:text-4xl font-light leading-tight mb-6 text-[#1A2E3A]" style={{ fontFamily: "'Inter', 'Noto Sans JP', sans-serif" }}>
              中小企業のための<br />オフィスITパートナー。
            </h2>
            <p className="text-[#5A7A8A] text-sm leading-loose tracking-wider mb-4">
              株式会社湘南ビジネスサポートは情報セキュリティを中心に、オフィスIT環境をワンストップで支援するITコンサルティング会社です。
            </p>
            <p className="text-[#5A7A8A] text-sm leading-loose tracking-wider mb-8">
              UTMを中心としたセキュリティ対策から、PC・複合機・ネットワーク構築、AI・DX活用まで、「何を導入すればいいかわからない」「誰に相談すればいいかわからない」そんな中小企業のIT・セキュリティの悩みを一社完結で解決します。
            </p>
            <div className="flex items-center gap-3">
              <div className="w-8 h-px bg-[#2B8DB8]" />
              <p className="text-[#2B8DB8] text-xs tracking-widest font-medium">神奈川県大和市 ／ 営業所</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── STRENGTHS ─── */}
      <section className="bg-[#EEF7FC] py-28 px-10 lg:px-20">
        <div ref={(el) => { sectionRefs.current[1] = el; }} className="opacity-0 translate-y-8 transition-all duration-700">
          <div className="text-center mb-16">
            <p className="text-[#2B8DB8] text-xs tracking-[0.4em] mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>WHY CHOOSE US</p>
            <h2 className="text-3xl lg:text-4xl font-light text-[#1A2E3A]" style={{ fontFamily: "'Inter', 'Noto Sans JP', sans-serif" }}>選ばれる3つの理由</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STRENGTHS.map((item, i) => (
              <div key={item.title} className="bg-white p-8 rounded-2xl hover:border-[#2B8DB8] border border-transparent transition-all duration-300 group" style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="w-12 h-12 flex items-center justify-center mb-6 rounded-full bg-[#DFF0FB] group-hover:bg-[#2B8DB8] transition-colors duration-300">
                  <i className={`${item.icon} text-[#2B8DB8] text-xl group-hover:text-white transition-colors duration-300`} />
                </div>
                <h3 className="text-base font-medium text-[#1A2E3A] mb-3 leading-snug">{item.title}</h3>
                <p className="text-[#5A7A8A] text-sm leading-loose">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SERVICES ─── */}
      <ServicesSection />

      {/* ─── TAGLINE ─── */}
      <section className="py-24 px-10 lg:px-20 bg-[#EEF7FC]">
        <div
          ref={(el) => { sectionRefs.current[2] = el; }}
          className="opacity-0 translate-y-8 transition-all duration-700 max-w-4xl mx-auto text-center"
        >
          <p className="text-[#2B8DB8] text-xs tracking-[0.4em] mb-8" style={{ fontFamily: "'Inter', sans-serif" }}>OUR MISSION</p>
          <h2
            className="text-3xl lg:text-5xl font-light text-[#1A2E3A] leading-tight mb-6"
            style={{ fontFamily: "'Inter', 'Noto Sans JP', sans-serif" }}
          >
            オフィスのIT、誰に聞けばいい？<br />
            <span className="text-[#2B8DB8]">そんな時の、相談窓口です。</span>
          </h2>
          <p className="text-[#5A7A8A] text-base leading-loose tracking-wider">
            IT・セキュリティからAI活用、集客支援まで。<br />
            中小企業のオフィスITを一社完結でサポートします。
          </p>
        </div>
      </section>

      {/* ─── COMPANY INFO ─── */}
      <section id="company-info" className="bg-white py-28 px-10 lg:px-20 border-t border-[#E8F4FB]">
        <div ref={(el) => { sectionRefs.current[3] = el; }} className="opacity-0 translate-y-8 transition-all duration-700 max-w-4xl mx-auto">
          <p className="text-[#2B8DB8] text-xs tracking-[0.4em] mb-4 text-center" style={{ fontFamily: "'Inter', sans-serif" }}>COMPANY INFO</p>
          <h2 className="text-3xl font-light text-[#1A2E3A] mb-12 text-center" style={{ fontFamily: "'Inter', sans-serif" }}>会社情報</h2>
          <div className="divide-y divide-[#E8F4FB]">
            {COMPANY_INFO.map((item) => (
              <div key={item.label} className="flex flex-col sm:flex-row py-5 gap-4">
                <span className="text-[#2B8DB8] text-xs tracking-widest sm:w-44 flex-shrink-0">{item.label}</span>
                <span className="text-[#1A2E3A] text-sm tracking-wider">{item.value}</span>
              </div>
            ))}
          </div>

          {/* 許認可・届出 */}
          <div className="mt-10 pt-8 border-t border-[#E8F4FB]">
            <p className="text-[#2B8DB8] text-xs tracking-[0.3em] mb-5" style={{ fontFamily: "'Inter', sans-serif" }}>許認可・届出</p>
            <div className="flex flex-col gap-3">
              {[
                { label: '古物商許可番号', value: '第452750021820号' },
                { label: '総務省届出番号', value: 'C2130299' },
              ].map((item) => (
                <div key={item.label} className="flex flex-col sm:flex-row gap-4">
                  <span className="text-[#5A7A8A] text-xs tracking-widest sm:w-44 flex-shrink-0">{item.label}</span>
                  <span className="text-[#1A2E3A] text-sm tracking-wider">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Google Map */}
          <div className="mt-10 pt-8 border-t border-[#E8F4FB]">
            <p className="text-[#2B8DB8] text-xs tracking-[0.3em] mb-5" style={{ fontFamily: "'Inter', sans-serif" }}>アクセス</p>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-map-pin-2-line text-[#2B8DB8] text-base" />
              </div>
              <span className="text-[#5A7A8A] text-xs tracking-wider">神奈川県大和市南林間２－１８－４</span>
            </div>
            <div className="w-full h-72 rounded-xl overflow-hidden border border-[#E8F4FB]">
              <iframe
                title="株式会社湘南ビジネスサポート 営業所"
                src={`https://maps.google.com/maps?q=${encodeURIComponent('神奈川県大和市南林間2-18-4')}&output=embed&hl=ja&z=17`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─── CONTACT ─── */}
      <section id="contact" className="py-28 px-10 lg:px-20 bg-[#F8FCFF]">
        <div ref={(el) => { sectionRefs.current[4] = el; }} className="opacity-0 translate-y-8 transition-all duration-700 max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#2B8DB8] text-xs tracking-[0.4em] mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>CONTACT</p>
            <h2 className="text-3xl lg:text-4xl font-light text-[#1A2E3A] mb-4" style={{ fontFamily: "'Inter', 'Noto Sans JP', sans-serif" }}>お問い合わせ</h2>
            <p className="text-[#5A7A8A] text-sm leading-loose max-w-xl mx-auto">
              「何から始めればいいかわからない」という段階でも大丈夫です。<br />初回のご相談は無料です。お気軽にどうぞ。
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2 flex flex-col gap-6">
              <div className="flex flex-col gap-5">
                {[
                  { icon: 'ri-map-pin-2-line', label: '営業所', text: '神奈川県大和市南林間２－１８－４' },
                  { icon: 'ri-time-line', label: '受付時間', text: '平日 9:00 〜 18:00' },
                  { icon: 'ri-shield-check-line', label: '無料相談', text: '初回のご相談は無料です。お気軽にどうぞ。' },
                ].map((info) => (
                  <div key={info.label} className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#DFF0FB] flex-shrink-0 mt-0.5">
                      <i className={`${info.icon} text-[#2B8DB8] text-base`} />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-[#1A2E3A] mb-1 tracking-wider">{info.label}</p>
                      <p className="text-sm text-[#5A7A8A] leading-loose">{info.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#C8E4F2] pt-6">
                <p className="text-xs text-[#5A7A8A] tracking-wider mb-3">対応サービス</p>
                <div className="flex flex-col gap-2">
                  {['オフィスIT機器・ネットワーク構築', '情報セキュリティ対策', 'IT・DX支援'].map((s) => (
                    <div key={s} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#2B8DB8] flex-shrink-0" />
                      <span className="text-xs text-[#1A2E3A] tracking-wider">{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              {formStatus === 'success' ? (
                <div className="flex flex-col items-center justify-center text-center py-20 bg-white rounded-2xl border border-[#C8E4F2] px-8">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#DFF0FB] mb-6">
                    <i className="ri-checkbox-circle-line text-3xl text-[#2B8DB8]" />
                  </div>
                  <h3 className="text-xl font-light text-[#1A2E3A] mb-3" style={{ fontFamily: "'Inter', 'Noto Sans JP', sans-serif" }}>送信が完了しました</h3>
                  <p className="text-sm text-[#5A7A8A] leading-loose mb-8">お問い合わせありがとうございます。<br />内容を確認後、担当者よりご連絡いたします。</p>
                  <button onClick={() => setFormStatus('idle')} className="px-8 py-3 bg-[#2B8DB8] text-white text-xs tracking-widest rounded-full hover:bg-[#1E6E94] transition-all duration-300 cursor-pointer whitespace-nowrap">
                    別の内容を送る
                  </button>
                </div>
              ) : (
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-[#C8E4F2] p-8 flex flex-col gap-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-medium text-[#1A2E3A] tracking-wider">会社名 <span className="text-[#2B8DB8]">*</span></label>
                      <input type="text" name="company" required placeholder="株式会社〇〇" className="border border-[#C8E4F2] rounded-lg px-4 py-3 text-sm text-[#1A2E3A] placeholder-[#B0C8D4] focus:outline-none focus:border-[#2B8DB8] transition-colors bg-[#F8FCFF]" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-medium text-[#1A2E3A] tracking-wider">お名前 <span className="text-[#2B8DB8]">*</span></label>
                      <input type="text" name="name" required placeholder="山田 太郎" className="border border-[#C8E4F2] rounded-lg px-4 py-3 text-sm text-[#1A2E3A] placeholder-[#B0C8D4] focus:outline-none focus:border-[#2B8DB8] transition-colors bg-[#F8FCFF]" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-medium text-[#1A2E3A] tracking-wider">メールアドレス <span className="text-[#2B8DB8]">*</span></label>
                      <input type="email" name="email" required placeholder="example@company.co.jp" className="border border-[#C8E4F2] rounded-lg px-4 py-3 text-sm text-[#1A2E3A] placeholder-[#B0C8D4] focus:outline-none focus:border-[#2B8DB8] transition-colors bg-[#F8FCFF]" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-medium text-[#1A2E3A] tracking-wider">電話番号</label>
                      <input type="tel" name="phone" placeholder="000-0000-0000" className="border border-[#C8E4F2] rounded-lg px-4 py-3 text-sm text-[#1A2E3A] placeholder-[#B0C8D4] focus:outline-none focus:border-[#2B8DB8] transition-colors bg-[#F8FCFF]" />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-medium text-[#1A2E3A] tracking-wider">お問い合わせ種別 <span className="text-[#2B8DB8]">*</span></label>
                    <div className="relative">
                      <select name="inquiry_type" required className="w-full border border-[#C8E4F2] rounded-lg px-4 py-3 text-sm text-[#1A2E3A] focus:outline-none focus:border-[#2B8DB8] transition-colors bg-[#F8FCFF] appearance-none cursor-pointer">
                        <option value="">選択してください</option>
                        {INQUIRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-[#5A7A8A] pointer-events-none" />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-[#1A2E3A] tracking-wider">お問い合わせ内容 <span className="text-[#2B8DB8]">*</span></label>
                      <span className={`text-xs ${charCount > 500 ? 'text-red-400' : 'text-[#5A7A8A]'}`}>{charCount} / 500</span>
                    </div>
                    <textarea
                      name="message" required rows={5} maxLength={500}
                      placeholder="お問い合わせ内容をご記入ください。&#13;&#10;例：現在のセキュリティ対策が不十分か確認したい、PC環境を見直したい、など"
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="border border-[#C8E4F2] rounded-lg px-4 py-3 text-sm text-[#1A2E3A] placeholder-[#B0C8D4] focus:outline-none focus:border-[#2B8DB8] transition-colors bg-[#F8FCFF] resize-none"
                    />
                  </div>
                  {formStatus === 'error' && (
                    <div className="flex items-center gap-2 text-red-400 text-xs bg-red-50 px-4 py-3 rounded-lg">
                      <i className="ri-error-warning-line" />
                      送信に失敗しました。時間をおいて再度お試しください。
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting' || charCount > 500}
                    className="w-full py-4 bg-[#2B8DB8] text-white text-sm tracking-widest rounded-lg hover:bg-[#1E6E94] transition-all duration-300 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {formStatus === 'submitting' ? (
                      <><i className="ri-loader-4-line animate-spin" />送信中...</>
                    ) : (
                      <>送信する <i className="ri-arrow-right-line" /></>
                    )}
                  </button>
                  <p className="text-[#5A7A8A] text-xs text-center leading-loose">
                    送信いただいた情報はお問い合わせへの回答にのみ使用します。<br />
                    <span className="text-[#B0C8D4]">* は必須項目です</span>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes fadeUp { to { opacity: 1; transform: translateY(0); } }
        .animate-in { opacity: 1 !important; transform: translateY(0) !important; }
      `}</style>
    </div>
  );
}
